import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueries } from '@tanstack/react-query';
import { AlertTriangle, Clock, Send, Loader2, CheckCircle2, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import ProposalCardShell from './ProposalCardShell';
import { routinesApi, type Routine } from '@/services/routines-api';
import { handleApiError } from '@/services/api';
import { humanReadableCron, nextNRuns } from '@/utils/cron';
import { useTimezone } from '@/contexts/TimezoneContext';
import type { ToolSegmentPayload } from '@/types/chat';

type Proposal = Extract<ToolSegmentPayload, { type: 'routine_proposal' }>;

interface Props {
  proposal: Proposal;
  /** The chat thread that birthed this proposal — sent as `birthed_from_chat_session_id` on Approve. */
  chatThreadId: string;
  /** Optional callback when the user clicks Edit — opens the parent's edit modal. */
  onEdit?: (proposal: Proposal) => void;
}

const ChannelChip: React.FC<{ label: string; locked?: boolean }> = ({ label, locked }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
      locked
        ? 'bg-accent border-primary/40 text-primary'
        : 'bg-muted border-border text-foreground'
    }`}
  >
    {label}
    {locked ? <span className="text-[9px] uppercase tracking-wide opacity-70">Always</span> : null}
  </span>
);

const RoutineProposalCard: React.FC<Props> = ({ proposal, chatThreadId, onEdit }) => {
  const navigate = useNavigate();
  const { timezone: orgTimezone } = useTimezone();
  const [dismissed, setDismissed] = useState(false);
  const [createdRoutine, setCreatedRoutine] = useState<Routine | null>(null);

  const tz = proposal.proposed.timezone || orgTimezone;
  const cron = proposal.proposed.schedule.expression;
  const next = useMemo(() => nextNRuns(cron, tz), [cron, tz]);
  const human = proposal.proposed.schedule.human_readable || humanReadableCron(cron);

  // Resolve overlap warnings if the agent flagged any existing routines.
  const conflictQueries = useQueries({
    queries: proposal.similar_existing_routine_ids.map((id) => ({
      queryKey: ['routine', id],
      queryFn: () => routinesApi.getRoutine(id),
      staleTime: 30_000,
    })),
  });
  const conflicts = conflictQueries
    .map((q, i) => ({ id: proposal.similar_existing_routine_ids[i], routine: q.data?.routine }))
    .filter((c): c is { id: string; routine: Routine } => !!c.routine);

  const approveMutation = useMutation({
    mutationFn: () =>
      routinesApi.createRoutine({
        name: proposal.proposed.name,
        schedule: cron,
        timezone: tz,
        message: proposal.proposed.derived_prompt,
        outputChannels: proposal.proposed.output_channels.map((c) => ({
          source: 'notification_channel' as const,
          id: c.id,
        })),
        birthed_from_chat_session_id: chatThreadId,
      }),
    onSuccess: ({ routine }) => {
      setCreatedRoutine(routine);
      toast.success('Routine scheduled');
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to schedule routine');
    },
  });

  if (dismissed) {
    return (
      <ProposalCardShell
        kicker="Routine proposal"
        title={proposal.proposed.name}
        actions={null}
        resolved={{ tone: 'muted', label: 'Dismissed' }}
      >
        <div />
      </ProposalCardShell>
    );
  }

  if (createdRoutine) {
    return (
      <ProposalCardShell
        kicker="Routine proposal"
        title={proposal.proposed.name}
        actions={null}
        resolved={{
          tone: 'success',
          label: '✓ Routine scheduled',
          action: (
            <button
              type="button"
              onClick={() => navigate(`/routines/${createdRoutine.id}`)}
              className="inline-flex items-center gap-1 text-xs font-medium text-success hover:underline"
            >
              View in Routines
              <ExternalLink className="w-3 h-3" />
            </button>
          ),
        }}
      >
        <div />
      </ProposalCardShell>
    );
  }

  return (
    <ProposalCardShell
      kicker="Routine proposal"
      title={proposal.proposed.name}
      subtitle={human}
      banner={
        conflicts.length > 0 ? (
          <div className="rounded-lg border border-warn/20 bg-warn/15 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-warn mt-0.5 flex-shrink-0" />
              <div className="text-xs text-warn space-y-1.5 flex-1">
                <div className="font-medium">
                  This may overlap with {conflicts.length} existing routine
                  {conflicts.length > 1 ? 's' : ''}:
                </div>
                <ul className="space-y-1">
                  {conflicts.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-2">
                      <span className="truncate">
                        <span className="font-medium">{c.routine.name}</span>
                        <span className="opacity-70"> — {humanReadableCron(c.routine.schedule)}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => navigate(`/routines/${c.id}`)}
                        className="text-xs font-medium text-warn hover:underline whitespace-nowrap"
                      >
                        Open existing
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : null
      }
      actions={
        <>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            disabled={approveMutation.isPending}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Dismiss
          </button>
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(proposal)}
              disabled={approveMutation.isPending}
              className="px-3 py-1.5 rounded-lg text-sm font-medium border border-input dark:border-white/10 text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
            >
              Edit details
            </button>
          )}
          <button
            type="button"
            onClick={() => approveMutation.mutate()}
            disabled={approveMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {approveMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="w-3.5 h-3.5" />
            )}
            Approve & schedule
          </button>
        </>
      }
    >
      <div className="rounded-lg bg-muted dark:bg-white/[0.03] border border-border p-3 text-sm text-foreground whitespace-pre-wrap font-mono leading-relaxed">
        {proposal.proposed.derived_prompt}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Schedule
          </div>
          <div className="text-foreground font-medium">{human}</div>
          <div className="text-xs text-muted-foreground font-mono mt-0.5">
            {cron} · {tz}
          </div>
          {next.length > 0 && (
            <ul className="text-xs text-muted-foreground mt-2 space-y-0.5">
              <li className="font-medium text-muted-foreground">Next runs:</li>
              {next.map((d, i) => (
                <li key={i}>
                  ·{' '}
                  {d.toLocaleString('en-US', {
                    timeZone: tz,
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
            <Send className="w-3 h-3" /> Delivers to
          </div>
          <div className="flex flex-wrap gap-1.5">
            <ChannelChip label="Chat" locked />
            {proposal.proposed.output_channels.length === 0 ? (
              <span className="text-xs text-muted-foreground self-center">
                Chat-only — add other channels in Edit details.
              </span>
            ) : (
              proposal.proposed.output_channels.map((c) => (
                <ChannelChip key={`${c.source}:${c.id}`} label={`${c.source}:${c.id}`} />
              ))
            )}
          </div>
        </div>
      </div>
    </ProposalCardShell>
  );
};

export default RoutineProposalCard;
